<template>
  <v-card-text class="text-left">
    
    <!-- Search Bar -->
    <v-text-field
      v-model="searchQuery"
      label="Search Call Recordings"
      outlined
      placeholder="Call ID, Agent (e.g., 12345), Duration (e.g., duration:>2 or duration:<5)"
      @keyup.enter="handleSearchInput"
      prepend-inner-icon="mdi-magnify"
      clearable
      :loading="isSearchingById"
      :error-messages="searchError"
      hide-details="auto"
      class="mb-4"
      style="flex: 1"
    ></v-text-field>

    <!-- Date Range Selector -->
    <v-sheet color="grey lighten-4 pa-4 mb-4" rounded>
      <div class="d-flex align-center flex-row">
        <div class="flex-1 text-center">
          <div class="font-weight-bold text-h6">{{ startDate | humanDate }} <span class="grey--text text--lighten-1 text-h6 mx-2">-</span> {{ endDate | humanDate }}</div>
        </div>

        <v-btn icon color="grey" @click="resetDateRange" title="Reset to last 30 days"><v-icon>mdi-refresh</v-icon></v-btn>
        
        <!-- Date Picker Menu -->
        <v-menu
          v-model="datePickerMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-x
          left
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              icon 
              color="grey" 
              v-bind="attrs" 
              v-on="on"
              title="Select date range"
            >
              <v-icon>mdi-calendar-range</v-icon>
            </v-btn>
          </template>
          
          <v-date-picker
            v-model="selectedDateRange"
            range
            color="primary"
            :allowed-dates="isDateAllowed"
            :max="maxSelectableDate"
            :min="minSelectableDate"
            @change="onDateRangeChange"
          ></v-date-picker>
        </v-menu>
      </div>
    </v-sheet>

    <!-- Main Loading Indicator (for date loading) -->
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-1" />

    <!-- Call Recording List - Use filteredRecordings -->
    <v-data-table
      v-if="!loading"
      :headers="headers"
      :items="filteredRecordings"
      :items-per-page="20"
      class="elevation-1"
      :no-data-text="noDataText"
      :loading="loading"
      loading-text="Loading recordings..."
      @click:row="openCallRecordingDetails"
      item-key="callId"
    >
       <!-- Slot for duration display using the formatted value -->
       <template v-slot:item.durationValue="{ item }">
           <span>{{ formatDurationForTable(item.durationValue) }}</span>
       </template>
    </v-data-table>

    <CallAnalysisCallDialog v-model="callRecordingDetailsDialog" :callId="selectedRecording?.callId" />

  </v-card-text>
</template>

<script>
import { DateTime } from 'luxon';
import { getFirestore, collection, where, getDoc, getDocs, query, doc, limit, orderBy, startAt, endAt } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import _ from 'lodash'; // Import lodash
import CallAnalysisCallDialog from '@/components/CustomerService/CallAnalysisCallDialog.vue';

const DATE_FORMAT = 'yyyy-MM-dd'; // Date format for both internal use and Firestore
const DURATION_REGEX = /duration:([<>])(\d+(\.\d+)?)/i; // Regex for duration:>/</d+ (case-insensitive)
const DEBOUNCE_WAIT = 500; // Milliseconds to wait after user stops typing

export default {
  name: 'CallAnalysisExplorer',
  components: {
    CallAnalysisCallDialog,
  },
  data() {
    // Set default date range to last 30 days (same as Trends page)
    const now = DateTime.now().minus({ days: 2 });
    const defaultStart = now.minus({ days: 30 });
    
    return {
      loading: true, // Start with loading state
      isSearchingById: false, // Specific loading state for direct ID lookup
      callRecordingDetailsDialog: false,
      searchQuery: '',
      conversationalStyle: true, // Default transcript view
      // Date range properties (similar to Trends page)
      dateRange: {
        start: defaultStart.toFormat('yyyy-MM-dd'),
        end: now.toFormat('yyyy-MM-dd')
      },
      startDate: defaultStart.toFormat('yyyy-MM-dd'),
      endDate: now.toFormat('yyyy-MM-dd'),
      selectedDateRange: [defaultStart.toFormat('yyyy-MM-dd'), now.toFormat('yyyy-MM-dd')],
      datePickerMenu: false,
      headers: [
        { text: 'Call ID', value: 'callId', sortable: true, width: '20%' },
        { text: 'Date', value: 'date', sortable: true, width: '15%' },
        { text: 'Main Category', value: 'categorization.main', sortable: true, width: '20%' },
        { text: 'Sub Category', value: 'categorization.sub', sortable: true, width: '25%' },
        { text: 'Agent', value: 'agent_name', sortable: true, width: '75px' },
        { text: 'Duration', value: 'durationValue', sortable: true, align: 'end', width: '100px' } // Sort by seconds
      ],
      allRecordingsForRange: [], // Store the full list for the selected date range
      selectedRecording: null,
      debouncedSearchHandler: null, // Debounced function instance
      // Search Filters State
      searchAgent: null,
      searchDuration: null, // { operator: '>', value: 120 } or { operator: '<', value: 300 } (in seconds)
      searchCallIdDirect: null, // Store call ID if it wasn't found locally
      searchError: null, // To display search-related errors below the text field
      // Audio URL
      audioUrl: null,
      // Dynamic date range from database
      minSelectableDate: null,
      maxSelectableDate: null,
    };
  },
  computed: {
    // --- Filtered Recordings for Data Table ---
    filteredRecordings() {
      // Use a temporary variable for the cleaned search query
      const cleanedSearchQuery = this.searchQuery?.trim().toLowerCase() || '';

      // Start with all recordings for the selected date range
      let items = [...this.allRecordingsForRange];

      // If there's no search query, no need to filter further
      if (!cleanedSearchQuery) {
        return items;
      }

      // Apply Agent Filter (if agent search term is active)
      if (this.searchAgent) {
        const lowerCaseAgent = this.searchAgent.toLowerCase();
        items = items.filter(rec => 
          rec.agent_name?.toLowerCase().includes(lowerCaseAgent) || 
          rec.agent?.toLowerCase().includes(lowerCaseAgent)
        );
      }
      // Apply Duration Filter (if duration search term is active)
      else if (this.searchDuration) {
        const targetSeconds = this.searchDuration.value;
        if (this.searchDuration.operator === '>') {
          items = items.filter(rec => rec.durationValue > targetSeconds);
        } else if (this.searchDuration.operator === '<') {
          items = items.filter(rec => rec.durationValue < targetSeconds);
        }
      }
      // General Text Search (if no specific filter is active)
      // This searches Call ID and Agent fields for the general query term
      else if (!this.searchCallIdDirect) { // Don't filter locally if a direct ID search is pending/active
        items = items.filter(rec => {
          const agentMatch = rec.agent_name?.toLowerCase().includes(cleanedSearchQuery) || 
                            rec.agent?.toLowerCase().includes(cleanedSearchQuery);
          const callIdMatch = rec.callId?.toLowerCase().includes(cleanedSearchQuery);
          return agentMatch || callIdMatch;
        });
      }
      // If searchCallIdDirect is set, it means we are looking for a specific ID globally,
      // so we don't want the local filter to preemptively show "no results". The table might
      // briefly show the previous state or be empty until the lookup completes.

      return items;
    },

    // --- Dynamic No Data Text ---
    noDataText() {
        if (this.loading || !this.minSelectableDate) return 'Loading recordings...';
        // Check if a search query exists and the *filtered* results are empty
        if (this.searchQuery?.trim() && this.filteredRecordings.length === 0 && !this.isSearchingById) {
             // Avoid showing "no match" while actively searching by ID
            if (this.searchError) return this.searchError; // Show specific error if available
            return `No recordings found matching your criteria for the selected date range`;
        }
        // Check if there were no recordings *at all* for the date range (before filtering)
        if (this.allRecordingsForRange.length === 0) {
             return `No call recordings found for the selected date range`;
        }
        // Default Vuetify text
        return 'No data available';
    }
  },
  methods: {
    async initialize() {
      await this.loadAvailableDates();
      this.loadDataForDateRange();
    },

    // Load available dates from the database (same as Trends page)
    async loadAvailableDates() {
      try {
        const db = getFirestore();
        const dailyCollection = collection(db, 'cc_callanalysis_daily');
        
        // Get all documents to determine available date range
        const dailyQuery = query(dailyCollection, limit(200));
        const dailySnapshot = await getDocs(dailyQuery);
        
        const availableDates = [];
        
        dailySnapshot.forEach(doc => {
          const docId = doc.id;
          if (this.isValidDateString(docId)) {
            availableDates.push(docId);
          }
        });
        
        if (availableDates.length > 0) {
          // Sort dates to find min and max
          availableDates.sort();
          this.minSelectableDate = availableDates[0];
          this.maxSelectableDate = availableDates[availableDates.length - 1];
          console.log(`Available date range: ${this.minSelectableDate} to ${this.maxSelectableDate}`);
          
          // Ensure the current date range is within the available range
          const startDateTime = DateTime.fromISO(this.dateRange.start);
          const endDateTime = DateTime.fromISO(this.dateRange.end);
          const minDateTime = DateTime.fromISO(this.minSelectableDate);
          const maxDateTime = DateTime.fromISO(this.maxSelectableDate);
          
          let rangeChanged = false;
          
          if (startDateTime < minDateTime) {
            this.dateRange.start = this.minSelectableDate;
            this.startDate = this.minSelectableDate;
            rangeChanged = true;
          }
          if (endDateTime > maxDateTime) {
            this.dateRange.end = this.maxSelectableDate;
            this.endDate = this.maxSelectableDate;
            rangeChanged = true;
          }
          
          if (rangeChanged) {
            this.selectedDateRange = [this.dateRange.start, this.dateRange.end];
          }
        }
      } catch (error) {
        console.error('Error loading available dates:', error);
        // Set loading to false even on error so user can see the error message
        this.loading = false;
      }
    },

    // Check if a date string is valid (same as Trends page)
    isValidDateString(dateString) {
      // Check if the string matches YYYY-MM-DD format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(dateString)) {
        return false;
      }
      
      // Check if it's a valid date
      const date = new Date(dateString);
      return date instanceof Date && !isNaN(date) && date.toISOString().substr(0, 10) === dateString;
    },

    // Method to check if a date is allowed (exists in database)
    async isDateAllowed(date) {
      if (!date) return false;
      
      try {
        const db = getFirestore();
        const docRef = doc(db, 'cc_callanalysis_daily', date);
        const docSnap = await getDoc(docRef);
        return docSnap.exists();
      } catch (error) {
        console.error('Error checking date availability:', error);
        return false;
      }
    },

    // Reset date range to last 30 days (same as Trends page)
    resetDateRange() {
      // Reset to the last 30 days
      const now = DateTime.now().minus({ days: 2 });
      const defaultStart = now.minus({ days: 30 });
      
      // Format for display and store for query (using YYYY-MM-DD to match DB)
      this.dateRange.start = defaultStart.toFormat('yyyy-MM-dd');
      this.dateRange.end = now.toFormat('yyyy-MM-dd');
      this.startDate = this.dateRange.start;
      this.endDate = this.dateRange.end;
      
      // Reset the selected date range for the picker
      this.selectedDateRange = [this.dateRange.start, this.dateRange.end];
      
      console.log('Date range reset:', this.dateRange.start, 'to', this.dateRange.end);
      
      this.datePickerMenu = false; // Close the menu if open
      this.loadDataForDateRange();
    },

    // Apply selected date range (same as Trends page)
    applySelectedDateRange() {
      if (!this.selectedDateRange || this.selectedDateRange.length !== 2) {
        console.warn('Please select both start and end dates');
        return;
      }

      // Sort the dates to ensure proper order
      const sortedDates = [...this.selectedDateRange].sort();
      
      this.dateRange.start = sortedDates[0];
      this.dateRange.end = sortedDates[1];
      this.startDate = this.dateRange.start;
      this.endDate = this.dateRange.end;
      
      this.datePickerMenu = false; // Close the menu
      this.loadDataForDateRange();
    },

    // Handle date range change (same as Trends page)
    onDateRangeChange(value) {
      this.selectedDateRange = value;
      
      // Auto-apply when both dates are selected
      if (value && value.length === 2) {
        this.applySelectedDateRange();
      }
    },

    // --- Debounced Search Logic ---
    handleSearchInput() {
      console.log('Search input handled:', this.searchQuery);
      this.searchError = null; // Clear previous errors
      // Reset specific filter states
      this.searchAgent = null;
      this.searchDuration = null;
      this.searchCallIdDirect = null;

      const query = this.searchQuery?.trim() || ''; // Use optional chaining and default

      if (!query) {
        // If search is cleared, computed property `filteredRecordings` handles showing all
        return;
      }

      const lowerCaseQuery = query.toLowerCase();

      // --- Filter Priority ---
      // 1. Check for Duration Filter
      const durationMatch = lowerCaseQuery.match(DURATION_REGEX);
      if (durationMatch) {
        const operator = durationMatch[1]; // '>' or '<'
        const minutes = parseFloat(durationMatch[2]);
        if (!isNaN(minutes) && minutes >= 0) {
          this.searchDuration = { operator, value: minutes * 60 };
          console.log('Applying duration filter:', this.searchDuration);
          return; // Handled as duration
        } else {
           this.searchError = 'Invalid duration value provided.';
           console.warn('Invalid duration value:', query);
           return; // Invalid duration format
        }
      }

      // 2. Check for Agent Filter (Example: Numeric)
      // Adjust this regex if your agent identifiers are different
      if (/^\d+$/.test(query)) {
        console.log('Applying agent filter (numeric assumption):', query);
        this.searchAgent = query;
        return; // Handled as agent
      }

      // --- Call ID Handling ---
      // 3. Define the *specific* regex for your Call ID format
      //    Format: F + 12 digits + V + 1 digit + U + 7 digits (Case-insensitive)
      const specificCallIdRegex = /^F\d{12}V\d{1}U\d{7}$/i;
      const isExactCallIdFormat = specificCallIdRegex.test(query);

      // 4. First, always check if the query exactly matches a Call ID *already loaded* for the current date range
      const foundLocally = this.allRecordingsForRange.find(rec => rec.callId.toLowerCase() === lowerCaseQuery);

      if (foundLocally) {
        // If found locally (even if it doesn't match the specific format, maybe an old ID?), open it
        console.log('Call ID found locally:', lowerCaseQuery);
        this.openCallRecordingDetails(foundLocally);
        // Don't return yet, let the computed filter potentially narrow down if needed,
        // although opening details usually makes filtering moot.
        // But we *don't* trigger a DB search if found locally.
      }
      // 5. If NOT found locally, THEN check if it matches the *specific Call ID format*
      else if (isExactCallIdFormat) {
          // Only trigger the database lookup if it wasn't found locally AND it fits the exact format
          console.log('Potential Call ID format matched, not found locally. Searching database for:', query);
          this.searchCallIdDirect = query; // Signal intent to search DB
          this.lookupCallById(query);      // Perform the DB lookup
          return; // Handled as specific Call ID DB lookup
      }

      // --- General Text Search Fallback ---
      // 6. If it wasn't duration, agent, found locally, or a specific format Call ID needing DB lookup,
      //    treat it as a general text search term (e.g., agent name, partial ID, keyword like "duration").
      //    The `filteredRecordings` computed property will handle applying this filter locally.
      console.log('Applying general text filter:', query);
      // No specific action needed here, computed property takes over.
    },

    // --- Firestore Direct Call ID Lookup ---
    async lookupCallById(callId) {
      if (!callId) return;
      this.isSearchingById = true;
      this.searchError = null;
      const db = getFirestore();
      // Ensure callId casing matches Firestore document ID if case-sensitive
      const callDocRef = doc(db, 'cc_callanalysis_calls', callId);

      try {
        const docSnap = await getDoc(callDocRef);

        if (docSnap.exists()) {
          console.log("Direct Call ID found in DB:", docSnap.id);
          const data = docSnap.data();
          const formattedRecording = this.formatFirestoreDoc(docSnap);
          formattedRecording.foundViaDirectSearch = true;
          formattedRecording.originalSearchDateRange = [this.dateRange.start, this.dateRange.end]; // Store context

          this.selectedRecording = formattedRecording;
          this.callRecordingDetailsDialog = true;
          // Successfully found, clear search query maybe? Or leave it?
          // this.searchQuery = ''; // Optional: Clear search after successful lookup
        } else {
          console.log("Call ID not found in database:", callId);
          this.searchError = `Call ID "${callId}" not found in the database.`;
        }
      } catch (error) {
        console.error("Error looking up call ID:", error);
        this.searchError = "An error occurred while searching for the Call ID.";
      } finally {
        this.isSearchingById = false;
        // Clear the direct search intention regardless of success/failure
        // so the computed filter doesn't stay blocked
        this.searchCallIdDirect = null;
      }
    },

    // --- Load Data for Selected Date Range ---
    async loadDataForDateRange() {
      if (!this.dateRange.start || !this.dateRange.end) {
           console.warn("No date range set");
           return;
      }

      // Don't start loading data until we have the available date range
      if (!this.minSelectableDate) {
        console.log("Waiting for available dates to load before loading data");
        return;
      }

      this.loading = true;
      this.allRecordingsForRange = []; // Clear previous data
      this.searchError = null; // Clear errors on date change

      const db = getFirestore();
      const recordingsRef = collection(db, 'cc_callanalysis_calls');
      
      console.log('Loading data for date range:', this.dateRange.start, 'to', this.dateRange.end);

      try {
        // Create an optimized query using Firestore's built-in range filtering
        // Since dates are stored as strings in YYYY-MM-DD format, we can use where clauses
        const q = query(
          recordingsRef,
          where('date', '>=', this.dateRange.start),
          where('date', '<=', this.dateRange.end),
          limit(1000) // Reasonable limit for call recordings
        );

        const querySnapshot = await getDocs(q);
        const loadedRecordings = [];
        querySnapshot.forEach((doc) => {
          loadedRecordings.push(this.formatFirestoreDoc(doc));
        });
        
        // Sort by date descending, then by time if available
        loadedRecordings.sort((a, b) => {
          const dateCompare = b.date.localeCompare(a.date);
          if (dateCompare !== 0) return dateCompare;
          // If dates are the same, sort by time if available
          if (a.time && b.time) {
            return b.time.localeCompare(a.time);
          }
          return 0;
        });
        
        this.allRecordingsForRange = loadedRecordings;
        console.log(`Loaded ${this.allRecordingsForRange.length} recordings for date range.`);
        // Trigger filtering based on existing search query, if any
        // (Computed property will react automatically)
      } catch (error) {
        console.error("Error loading data for date range:", error);
        this.searchError = "Failed to load recordings for the selected date range.";
        this.allRecordingsForRange = []; // Ensure empty on error
      } finally {
        this.loading = false;
      }
    },

    // --- Helper to format Firestore Doc Data ---
    formatFirestoreDoc(doc) {
        const data = doc.data();
        const duration = data.duration; // Assume duration is in seconds
        return {
            callId: doc.id,
            language: data.language || null,
            agent: data.agent || null,
            agent_name: data.agent_name || data.agent || null, // Support both field names
            date: data.date || null, // Store original date format from DB
            durationValue: typeof duration === 'number' ? duration : null, // Store raw duration in seconds
            duration: this.formatDuration(duration), // Formatted M:SS
            recordings: data.recordings || null, // Handle missing fields
            transcriptions: data.transcriptions || { original: '' }, // Ensure object exists
            conversation: data.conversation || [], // Ensure array exists
            phoneNumber: data.phone_number || null,
            // Handle categorization object structure
            categorization: {
                main: data.categorization?.main || null,
                sub: data.categorization?.sub || null
            },
            category: data.category || null, // Keep for backward compatibility
            time: data.time || null,
        };
    },

    // --- Formatting and Dialog ---
    formatDuration(seconds) {
      if (seconds == null || isNaN(seconds) || seconds < 0) {
        return '--:--'; // Indicate missing or invalid data
      }
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      const paddedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;
      return `${minutes}:${paddedSeconds}`;
    },

    formatDurationForTable(seconds) {
      if (seconds == null || isNaN(seconds) || seconds < 0) {
        return '--'; // Indicate missing or invalid data
      }
      const totalSeconds = Math.round(seconds);
      const minutes = Math.floor(seconds / 60);
      return `${totalSeconds}s (${minutes}m)`;
    },

    async openCallRecordingDetails(item) {
      console.log("Opening details for:", item);
      this.selectedRecording = { ...item }; // Make a copy to avoid potential mutation issues
       // Ensure duration is formatted correctly in the selected item
      this.selectedRecording.duration = this.formatDuration(item.durationValue);
      // Reset conversation style preference for each opened dialog
      this.conversationalStyle = true;
      // Load the audio file from GCP storage
      const path = `customer_service/call_analysis/recordings/${item.callId}.mp3`;
      try {
        const storage = getStorage();
        const storageRef = ref(storage, path);
        const url = await getDownloadURL(storageRef);
        console.log("URL:", url);
        this.audioUrl = url;
      } catch (error) {
        console.error("Error loading audio URL:", error);
      }
      this.callRecordingDetailsDialog = true;
    },

    // --- Text Highlighting Helper (Example) ---
    highlightText(text) {
        if (!text || !this.searchQuery?.trim() || this.searchDuration || this.searchAgent || this.searchCallIdDirect) {
            // Don't highlight if search is empty, or if it was a specific filter type (duration/agent/ID)
            return text;
        }
        // Simple highlighting for the general search term
        const query = this.searchQuery.trim();
        const regex = new RegExp(`(${_.escapeRegExp(query)})`, 'gi'); //lodash escape + global, case-insensitive
        return text.replace(regex, '<mark>$1</mark>');
    }

  },
  filters: {
    humanDate(dateString) {
      if (!dateString || typeof dateString !== 'string') {
        return 'Select Date';
      }
      try {
        // Parse ISO date and format for display
        return DateTime.fromISO(dateString).toFormat('MMMM d, yyyy');
      } catch (e) {
        console.error("Error formatting date in filter:", dateString, e);
        return 'Invalid Date';
      }
    },
    // Basic name removal - adjust regex if names are more complex
    removeName(text) {
       if (typeof text !== 'string') return '';
      return text.replace(/^.+?: /, ''); // Removes "SpeakerName: " from the beginning
    },
    secondsToMinutes(seconds) {
      if (seconds == null || isNaN(seconds) || seconds < 0) {
        return '--:--'; // Indicate missing or invalid data
      }
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      const paddedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;
      return `${minutes}:${paddedSeconds}`;
    }
  },
  watch: {
    // --- Watch Search Query for Debounced Handling ---
    searchQuery(newValue) {
      this.searchError = null; // Clear error on new input
      this.debouncedSearchHandler(newValue); // Call the debounced function
    },
  },
  created() {
    // --- Create the debounced function instance ---
    this.debouncedSearchHandler = _.debounce(this.handleSearchInput, DEBOUNCE_WAIT);
    
    console.log('Initial date range set in created():', this.dateRange.start, 'to', this.dateRange.end);
  },
  mounted() {
    this.initialize(); // Load data for the initial date range on mount
  },
  // --- Cleanup Debounce ---
  beforeDestroy() {
    // Cancel any pending debounced calls if the component is destroyed
    if (this.debouncedSearchHandler) {
      this.debouncedSearchHandler.cancel();
    }
  }
}
</script>

<style lang="scss" scoped>
/* Style the date button to look less like a standard button */
.v-btn--text.v-btn--active::before, .v-btn--text:hover::before {
  opacity: 0.08;
}

/* Improve readability of raw transcript */
pre {
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 0.9em;
  color: #333;
  max-height: 400px; /* Consistent max height */
  overflow-y: auto;
}

/* Highlight style */
::v-deep mark {
    background-color: #FFF59D; /* Light yellow highlight */
    padding: 0.1em;
    border-radius: 3px;
    box-shadow: 1px 1px 0 #E0E0E0;
}

/* Ensure vertical dividers have some height */
.v-divider--vertical {
    min-height: 100px; /* Adjust as needed */
}

/* Prevent duration text from wrapping */
.duration-text {
    white-space: nowrap;
    font-family: monospace; /* Use monospace font for better alignment */
}
</style>